import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sun, Moon, Minus, Plus, Timer, X, List, Wifi } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

function useWakeLock(active) {
  const lockRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    let released = false;
    (async () => {
      try {
        if ("wakeLock" in navigator) {
          lockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch {}
    })();
    const onVis = async () => {
      if (document.visibilityState === "visible" && !released) {
        try {
          if ("wakeLock" in navigator) lockRef.current = await navigator.wakeLock.request("screen");
        } catch {}
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVis);
      lockRef.current?.release?.().catch(() => {});
    };
  }, [active]);
}

function useTimer() {
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [running]);
  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return (h ? `${h}:${m.toString().padStart(2, "0")}` : `${m}`) + `:${sec.toString().padStart(2, "0")}`;
  };
  return { elapsed, running, setRunning, reset: () => setElapsed(0), fmt };
}

function splitSections(html, outline) {
  // Prefer explicit outline; else split by H2/H3; else return the whole content.
  const clean = (html || "").trim();
  if (outline && outline.length > 0) {
    // Try to split body by outline titles if present
    const sections = [];
    let remaining = clean;
    outline.forEach((title, idx) => {
      const safe = title.replace(/[-\\/\\^$*+?.()|[\]{}]/g, "\\$&");
      const re = new RegExp(`<h[23][^>]*>\\s*${safe}\\s*</h[23]>`, "i");
      const match = remaining.match(re);
      if (match) {
        const before = remaining.slice(0, match.index);
        if (idx === 0 && before.trim()) sections.push({ title: "Introducción", content: before });
        remaining = remaining.slice(match.index + match[0].length);
        const nextTitle = outline[idx + 1];
        if (nextTitle) {
          const safe2 = nextTitle.replace(/[-\\/\\^$*+?.()|[\]{}]/g, "\\$&");
          const re2 = new RegExp(`<h[23][^>]*>\\s*${safe2}\\s*</h[23]>`, "i");
          const m2 = remaining.match(re2);
          if (m2) {
            sections.push({ title, content: remaining.slice(0, m2.index) });
            remaining = remaining.slice(m2.index);
          } else {
            sections.push({ title, content: remaining });
            remaining = "";
          }
        } else {
          sections.push({ title, content: remaining });
          remaining = "";
        }
      } else {
        sections.push({ title, content: "" });
      }
    });
    if (sections.length > 0) return sections;
  }
  // Split by H2
  const re = /(<h2[^>]*>[\s\S]*?<\/h2>)/i;
  const parts = clean.split(/<h2[^>]*>/i);
  if (parts.length <= 1) return [{ title: "Prédica", content: clean }];
  const sections = [];
  if (parts[0].trim()) sections.push({ title: "Introducción", content: parts[0] });
  for (let i = 1; i < parts.length; i++) {
    const [titleRaw, ...rest] = parts[i].split(/<\/h2>/i);
    sections.push({ title: titleRaw.replace(/<[^>]+>/g, "").trim() || `Punto ${i}`, content: rest.join("</h2>") });
  }
  return sections;
}

const LS_KEY = (id) => `pulpit_cache:${id}`;

export default function Pulpit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [idx, setIdx] = useState(0);
  const [dark, setDark] = useState(false);
  const [fontSize, setFontSize] = useState(28);
  const [drawer, setDrawer] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const timer = useTimer();
  useWakeLock(true);

  // Try online + localStorage fallback for offline support
  useEffect(() => {
    const cached = localStorage.getItem(LS_KEY(id));
    if (cached) {
      try { setS(JSON.parse(cached)); setOfflineReady(true); } catch {}
    }
    (async () => {
      try {
        const r = await api.get(`/admin/sermons/${id}`);
        setS(r.data);
        localStorage.setItem(LS_KEY(id), JSON.stringify(r.data));
        setOfflineReady(true);
      } catch {
        // try public endpoint
        try {
          const r = await api.get(`/sermons/${id}`);
          setS(r.data);
          localStorage.setItem(LS_KEY(id), JSON.stringify(r.data));
          setOfflineReady(true);
        } catch (e) {
          if (!cached) toast.error("No se pudo cargar la prédica");
        }
      }
    })();
  }, [id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => Math.min(sections.length - 1, i + 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      else if (e.key === "Escape") nav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [s, idx]);

  if (!s) {
    return <div className="pulpit"><div className="pulpit-main">Cargando…</div></div>;
  }

  const sections = splitSections(s.body_html, s.outline);
  const section = sections[idx] || sections[0];

  return (
    <div className={`pulpit ${dark ? "dark" : ""}`} data-testid="pulpit-view">
      <header className="flex items-center gap-2 px-4 md:px-6 py-3 border-b border-[rgba(31,74,64,0.1)]" style={{ borderColor: dark ? "rgba(243,206,121,0.15)" : "rgba(31,74,64,0.1)" }}>
        <button className="btn-ghost !min-h-10" onClick={() => nav(-1)} aria-label="Salir" data-testid="btn-pulpit-exit"><X size={18} /></button>
        <button className="btn-ghost !min-h-10" onClick={() => setDrawer((d) => !d)} data-testid="btn-pulpit-index"><List size={18} /> Índice</button>
        <div className="font-serif text-sm md:text-base ml-2 truncate flex-1" style={{ color: dark ? "#F4F2EC" : "#13302A" }}>
          {s.title} {s.bible_reference ? `· ${s.bible_reference}` : ""}
        </div>
        <span className="text-xs flex items-center gap-1 opacity-70" data-testid="offline-status">
          <Wifi size={12} /> {offlineReady ? "Offline listo" : "Cargando…"}
        </span>
      </header>

      <main className="pulpit-main">
        {drawer && (
          <aside className="fixed top-14 left-0 bottom-0 w-72 bg-white dark:bg-[#14221E] border-r z-10 overflow-auto p-4" style={{ background: dark ? "#14221E" : "#fff", borderColor: dark ? "rgba(243,206,121,0.2)" : "rgba(31,74,64,0.12)" }}>
            <div className="eyebrow mb-3" style={{ color: dark ? "#F3CE79" : "#1F4A40" }}>Índice</div>
            <ul className="space-y-1">
              {sections.map((sec, i) => (
                <li key={i}>
                  <button
                    className={`w-full text-left px-3 py-2 rounded-lg transition ${i === idx ? "bg-[#1F4A40] text-white" : "hover:bg-[rgba(31,74,64,0.08)]"}`}
                    style={i === idx && dark ? { background: "#F3CE79", color: "#1a1308" } : null}
                    onClick={() => { setIdx(i); setDrawer(false); }}
                    data-testid={`drawer-sec-${i}`}
                  >
                    <span className="font-mono text-xs opacity-70 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif">{sec.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        )}
        <div className="max-w-4xl mx-auto">
          <div className="eyebrow mb-3" style={{ color: dark ? "#F3CE79" : "#E2B85C" }}>Sección {idx + 1} de {sections.length}</div>
          <h1 className="font-serif font-bold tracking-tight mb-4" style={{ fontSize: `${fontSize * 1.4}px`, color: dark ? "#F3CE79" : "#13302A" }} data-testid="pulpit-section-title">
            {section.title}
          </h1>
          <article
            className="font-serif leading-loose"
            style={{ fontSize: `${fontSize}px` }}
            data-testid="pulpit-content"
            dangerouslySetInnerHTML={{ __html: section.content || "<p style='opacity:0.6'>Sin contenido en esta sección.</p>" }}
          />
        </div>
      </main>

      <div className="controls">
        <button className="big-btn ghost" onClick={() => setFontSize((f) => Math.max(16, f - 2))} aria-label="A-" data-testid="btn-font-dec"><Minus size={16} /> A</button>
        <button className="big-btn ghost" onClick={() => setFontSize((f) => Math.min(60, f + 2))} aria-label="A+" data-testid="btn-font-inc">A <Plus size={16} /></button>
        <button className="big-btn ghost" onClick={() => setDark((d) => !d)} data-testid="btn-pulpit-theme">{dark ? <Sun size={18} /> : <Moon size={18} />}</button>
        <button className="big-btn ghost" onClick={() => { timer.setRunning((r) => !r); }} data-testid="btn-pulpit-timer">
          <Timer size={18} /> <span className="font-mono">{timer.fmt(timer.elapsed)}</span>
        </button>
        <button className="big-btn" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="btn-pulpit-prev"><ChevronLeft size={22} /> Anterior</button>
        <button className="big-btn" disabled={idx >= sections.length - 1} onClick={() => setIdx((i) => Math.min(sections.length - 1, i + 1))} data-testid="btn-pulpit-next">Siguiente <ChevronRight size={22} /></button>
      </div>
    </div>
  );
}
